import { A } from "components/A";
import type { NextPage } from "next";
import { generateNextSeo } from "next-seo/pages";
import Head from "next/head";
import React, { ReactNode } from "react";

interface GroupProps {
  name: string;
  entries: ReactNode[];
  className?: string;
}

const Group: React.FC<GroupProps> = ({ name, entries, className }) => {
  return (
    <div className={className}>
      <h3 className="mb-2 ml-0.5 text-2xl font-medium">{name}</h3>
      <ul>
        {entries.map((e, i) => (
          <li
            key={i}
            className="mb-1 first-letter:align-middle first-letter:text-3xl"
          >
            {e}
          </li>
        ))}
      </ul>
    </div>
  );
};

const Home: NextPage = () => {
  return (
    <div className="container mb-10">
      <Head>
        {generateNextSeo({
          title: "Commissions",
          openGraph: {
            images: [{ url: process.env.NEXT_PUBLIC_URL + "/commissions.jpg" }],
          },
          twitter: { cardType: "summary_large_image" },
        })}
      </Head>

      <h1 className="mt-3 mb-2 text-3xl font-medium">Commissions info</h1>

      <p className="mb-4">
        Have you ever wanted to commission me? Here&apos;s what you need to
        know:
      </p>

      <div className="mb-8 grid gap-10 gap-y-6 md:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3">
        <Group
          className="md:col-span-3 lg:col-span-2 xl:col-span-1"
          name="Status"
          entries={[
            <>
              ❌ commissions are <span className="font-medium">closed</span>
            </>,
            "🤔 I don't know when they will be open",
          ]}
        />
        <Group
          className="md:col-span-2 lg:col-span-1"
          name="Price & payment"
          entries={[
            <>
              💰 base price is <span className="font-medium">€200</span> (or
              equivalent in USD)
            </>,
            "🕔 I'll start drawing after receiving payment",
            // "💸 payment via PayPal",
            <>
              💬 contact via Discord{" "}
              <span className="font-medium">@irisarco</span>
            </>,
          ]}
        />
        <Group
          name="You will get"
          entries={[
            "✅ fully shaded image",
            "✅ with a background",
            <>
              ✅{" "}
              <A href="https://altv.irisarco.art" target="_blank">
                AltV file
              </A>{" "}
              (if applicable)
            </>,
            "❌ source file",
          ]}
        />
      </div>

      <h2 className="mb-2 text-3xl font-medium">Other info</h2>

      <p>
        There are things I like to draw, and there are things I don&apos;t like
        at all.
      </p>
      <p>
        You have the highest chance for a commission if your idea fits my usual
        style.
      </p>
      <p>
        The list below should answer some of your questions, don&apos;t be
        afraid to ask if you are not sure.
      </p>

      <div className="mt-6 grid gap-10 gap-y-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        <Group
          name="Complexity"
          entries={[
            "🆓 ¾ shot - from knees up",
            "😃 1 character",
            "🙂 2 characters (+50% price)",
            "🙂 alternate versions",
            "😕 more characters",
            "🤔 ask for detailed info",
          ]}
        />
        <Group
          name="Themes/fetishes"
          entries={[
            "😍 exhibitionism/public nudity",
            "😍 slutty outfits/activities",
            "😖 very unnatural proportions",
            "🤮 pissing, scat, gore, etc",
            "🤔 ask if not sure",
          ]}
        />
        <Group
          name="Characters"
          entries={[
            "😃 canon characters",
            "🙂 original characters",
            "😐 canon x oc",
            "😡 oc x Rainbow Dash",
          ]}
        />
        <Group
          name="Species"
          entries={["😃 anthro ponies", "😕 anything else"]}
        />
        <Group
          name="Other"
          entries={[
            "😃 females",
            "🙂 futa",
            "😕 males",
            "😖 solo males and gay stuff",
          ]}
        />
      </div>
    </div>
  );
};

export default Home;
